/**
 * L1 §2 — "Failures now": the live equivalent of the manual Excel export, and
 * the queue the rota actually works from.
 *
 * Three deliberate differences from the export it replaces:
 *
 * 1. **Missed jobs are in the list.** The manual workflow exports *errors* from
 *    the last 24 hours, so a job that silently stopped being scheduled produces
 *    no row at all (concept §2.1). Here it is a row, tinted danger, labelled
 *    "did not run".
 * 2. **So are jobs that never ran.** An enabled job with no session on record
 *    has nothing to restore from, which is worse than an old restore point and
 *    equally invisible to an error export.
 * 3. **Every row is annotatable.** The job name opens the L2 drawer, where the
 *    acknowledgement form stores the comment and resolution that used to live
 *    in the spreadsheet's `Failure Comments` / `Solution` columns (concept §5).
 *
 * Altitude: at most 10 rows here (design rules §1, the altitude test); the full
 * list is one click deeper at `/backup/jobs`.
 */

import { DataGrid, EmptyState } from "@norican/react";

import type { BackupJobRow } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";
import { DrillLink } from "@/lib/drill";

import { jobColumns } from "./job-columns";
import { PanelCard } from "./panel-card";
import { BACKUP_JOBS_PATH, RESULT_PARAM, SITE_PARAM, SOURCE_PARAM } from "./params";
import { needsAttention } from "./status";

/** How many rows the L1 panel shows — the rest live at L2. */
const WORST_LIMIT = 10;

/** Worst first: nothing to restore from, then silently absent, then errored. */
const ATTENTION_RANK: Record<string, number> = {
  "never-run": 0,
  missed: 1,
  failed: 2,
};

interface FailuresSectionProps {
  jobs: BackupJobRow[];
  /**
   * True when a source has stopped reporting. An empty failure list then means
   * "no failure has been *reported*", which is not the same claim as "nothing
   * is failing" — see the empty state below.
   */
  stale: boolean;
  sourceIds: string[];
  siteIds: string[];
}

export function FailuresSection({ jobs, stale, sourceIds, siteIds }: FailuresSectionProps) {
  const scoped = jobs.filter(
    (job) =>
      (sourceIds.length === 0 || sourceIds.includes(job.source)) &&
      (siteIds.length === 0 || (job.site != null && siteIds.includes(job.site))),
  );

  const unresolved = scoped
    .filter((job) => needsAttention(job.status))
    .sort((left, right) => {
      const rank = (ATTENTION_RANK[left.status] ?? 9) - (ATTENTION_RANK[right.status] ?? 9);
      if (rank !== 0) {
        return rank;
      }
      // Then oldest proven backup first — the longest-standing risk leads.
      return (left.last_success ?? "").localeCompare(right.last_success ?? "");
    });

  const drillParams = {
    ...(sourceIds.length > 0 ? { [SOURCE_PARAM]: sourceIds } : {}),
    ...(siteIds.length > 0 ? { [SITE_PARAM]: siteIds } : {}),
  };

  return (
    <section id="backup-failures" className="scroll-mt-6">
      <PanelCard
        title="Failures now"
        caption={
          <DrillLink
            to={BACKUP_JOBS_PATH}
            params={{ ...drillParams, [RESULT_PARAM]: ["failed", "missed", "never-run"] }}
            className="text-accent text-sm font-medium hover:underline"
          >
            View all {formatCount(unresolved.length)} →
          </DrillLink>
        }
        padding={unresolved.length === 0 ? "md" : "none"}
      >
        {unresolved.length === 0 ? (
          /* An all-clear is a claim about *now*, so it may only be made from
             current data. When a collector has gone quiet this list is empty
             because nothing is arriving, not because nothing is wrong — the one
             confusion the spreadsheet made for years and this board exists to
             end. Same grey, same wording discipline as the heat strip's silent
             days: absence is stated, never rendered as health. */
          <EmptyState
            size="sm"
            title={
              stale
                ? "No failures reported — but collection has stopped"
                : "Nothing is failing, and nothing is missing a run"
            }
            description={
              stale
                ? "This list only shows what the last report carried. A job that has failed since then is not here, so this is not an all-clear. See the collector status at the top of the board."
                : "Every enabled job in the current scope produced a session and completed it."
            }
          />
        ) : (
          <DataGrid<BackupJobRow>
            data={unresolved.slice(0, WORST_LIMIT)}
            getRowId={(job) => `${job.source}:${job.name}`}
            density="compact"
            aria-label={`The ${Math.min(unresolved.length, WORST_LIMIT)} jobs needing attention`}
            columns={jobColumns({ drillParams })}
          />
        )}
      </PanelCard>
    </section>
  );
}
